import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  MapPin,
  CurrencyDollar,
  Storefront,
  SignOut,
  CaretRight,
  CalendarBlank,
  Eye,
  Trash,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const COMPANY_LOGO = process.env.REACT_APP_COMPANY_LOGO;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayActivity, setTodayActivity] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: "", userName: "" });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const storedAdmin = localStorage.getItem("admin");
    const token = localStorage.getItem("adminToken");
    
    if (!token || !storedAdmin) {
      navigate("/admin/login");
      return;
    }
    
    setAdmin(JSON.parse(storedAdmin));
    fetchTodayActivity(token);
  }, [navigate]);

  const fetchTodayActivity = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/activity/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayActivity(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("admin");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load activity data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    navigate("/admin/login");
  };

  const handleDeleteSalesperson = async () => {
    const token = localStorage.getItem("adminToken");
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/salesperson/${deleteDialog.userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Salesperson deleted successfully");
      setDeleteDialog({ open: false, userId: "", userName: "" });
      fetchTodayActivity(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete salesperson");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src={COMPANY_LOGO} alt="Logo" className="h-8 object-contain" />
            <span className="text-xs px-2 py-1 bg-[#0055FF] rounded-full font-medium">ADMIN</span>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-[#9CA3AF] hover:text-white transition-colors"
            data-testid="admin-logout-button"
          >
            <SignOut size={24} weight="bold" />
          </button>
        </div>
        <div>
          <p className="text-[#9CA3AF] text-sm">Welcome back,</p>
          <h1 className="text-xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
            {admin?.name || "Admin"}
          </h1>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Today's Date */}
        <div className="flex items-center gap-2 text-[#4B5563]">
          <CalendarBlank size={20} weight="fill" />
          <span className="font-medium">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </span>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="stat-card" data-testid="stat-total-visits">
            <MapPin size={24} className="text-[#10B981] mb-2" weight="fill" />
            <p className="stat-value">{todayActivity?.summary?.total_visits || 0}</p>
            <p className="stat-label">Visits Today</p>
          </div>
          <div className="stat-card" data-testid="stat-total-sales">
            <CurrencyDollar size={24} className="text-[#F59E0B] mb-2" weight="fill" />
            <p className="stat-value">${todayActivity?.summary?.total_sales?.toLocaleString() || 0}</p>
            <p className="stat-label">Sales Today</p>
          </div>
          <div className="stat-card" data-testid="stat-new-customers">
            <Storefront size={24} className="text-[#0055FF] mb-2" weight="fill" />
            <p className="stat-value">{todayActivity?.summary?.total_customers_added || 0}</p>
            <p className="stat-label">New Customers</p>
          </div>
          <div className="stat-card" data-testid="stat-active-salespeople">
            <Users size={24} className="text-[#8B5CF6] mb-2" weight="fill" />
            <p className="stat-value">{todayActivity?.summary?.active_salespeople || 0}</p>
            <p className="stat-label">Active Salespeople</p>
          </div>
        </div>

        {/* Salespeople Activity */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Salespeople Activity
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/reports")}
              className="text-sm"
              data-testid="view-reports-button"
            >
              View Reports
            </Button>
          </div>

          {todayActivity?.salespeople?.length > 0 ? (
            <div className="card-flat overflow-hidden">
              {todayActivity.salespeople.map((person, index) => (
                <div
                  key={person.user_id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB] transition-colors"
                  data-testid={`salesperson-row-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => navigate(`/admin/salesperson/${person.user_id}`)}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center">
                        <span className="font-bold text-[#0055FF]">
                          {person.user_name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A0A0A]">{person.user_name}</p>
                        <p className="text-sm text-[#4B5563]">{person.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteDialog({ open: true, userId: person.user_id, userName: person.user_name });
                        }}
                        className="p-2 text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                        data-testid={`delete-salesperson-${index}`}
                      >
                        <Trash size={20} />
                      </button>
                      <CaretRight 
                        size={20} 
                        className="text-[#9CA3AF] cursor-pointer" 
                        onClick={() => navigate(`/admin/salesperson/${person.user_id}`)}
                      />
                    </div>
                  </div>
                  
                  <div 
                    className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-[#E5E7EB] cursor-pointer"
                    onClick={() => navigate(`/admin/salesperson/${person.user_id}`)}
                  >
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#0A0A0A]">{person.visits_today}</p>
                      <p className="text-xs text-[#9CA3AF]">Visits</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#10B981]">${person.sales_today}</p>
                      <p className="text-xs text-[#9CA3AF]">Sales</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#0A0A0A]">{person.customers_added_today}</p>
                      <p className="text-xs text-[#9CA3AF]">Customers</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#0A0A0A]">{person.schedules_completed_today}</p>
                      <p className="text-xs text-[#9CA3AF]">Completed</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-flat p-8 text-center">
              <Users size={48} className="mx-auto text-[#D1D5DB] mb-2" />
              <p className="text-[#9CA3AF]">No salespeople registered yet</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/reports")}
            className="h-auto py-4 flex flex-col items-center gap-2"
            data-testid="reports-button"
          >
            <CalendarBlank size={24} className="text-[#0055FF]" />
            <span>Date Reports</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/customer-search")}
            className="h-auto py-4 flex flex-col items-center gap-2"
            data-testid="customer-search-button"
          >
            <MagnifyingGlass size={24} className="text-[#10B981]" />
            <span>Customer Activity</span>
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Salesperson</DialogTitle>
            <DialogDescription asChild>
              <div>
                Are you sure you want to delete <strong>{deleteDialog.userName}</strong>? 
                This will permanently delete the salesperson account and ALL their data including:
                <ul className="list-disc ml-6 mt-2 text-left">
                  <li>All customers</li>
                  <li>All visits and photos</li>
                  <li>All phone call logs</li>
                  <li>All sales records</li>
                  <li>All schedules</li>
                </ul>
                <p className="mt-2 text-red-600 font-medium">This action cannot be undone.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, userId: "", userName: "" })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSalesperson} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete Salesperson"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
